import { useEffect, useState } from "react";
import { Cake, Heart, Phone, Calendar as CalendarIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDate } from "@/lib/format";

const MONTH_NAMES_ES = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
const MONTH_NAMES_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function AdminCelebrations() {
  const { t, lang } = useI18n();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ today: { birthdays: [], anniversaries: [], total: 0 }, current_month: { birthdays: [], anniversaries: [], total: 0, month: 0 } });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/celebrations");
        if (!cancelled) setData(data);
      } catch (_) {} finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const c = t.admin.celebrationsPage;
  const monthName = (lang === "es" ? MONTH_NAMES_ES : MONTH_NAMES_EN)[(data.current_month.month || 1) - 1] || "";

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-serif-display text-2xl font-semibold">{c.title}</h1>
        <p className="text-sm text-muted-foreground">{c.subtitle}</p>
      </div>

      {/* Today */}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-serif-display text-lg font-semibold flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary"><Cake className="h-4 w-4" /></span>
            {c.todayTitle}
          </h2>
          <Badge variant="default" className="rounded-full" data-testid="celebrations-today-count">{data.today.total}</Badge>
        </div>

        {loading ? (
          <Skeleton className="h-24 w-full rounded-2xl" />
        ) : data.today.total === 0 ? (
          <Card className="rounded-2xl border-dashed bg-white/60">
            <CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="celebrations-today-empty">{c.todayEmpty}</CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.today.birthdays.map((row) => (
              <CelebrationCard
                key={`b-${row.id}`}
                kind="birthday"
                title={row.full_name}
                subtitle={`${c.turning} ${row.age_on_birthday} ${c.years}`}
                phone={row.phone}
                date={row.date}
                lang={lang}
                accentToday
              />
            ))}
            {data.today.anniversaries.map((row) => (
              <CelebrationCard
                key={`a-${row.id}`}
                kind="anniversary"
                title={row.full_name}
                subtitle={`${c.celebrating} ${row.years_on_anniversary} ${c.anniversaryYears}`}
                phone={row.phone}
                date={row.date}
                lang={lang}
                accentToday
              />
            ))}
          </div>
        )}
      </section>

      {/* Current month */}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-serif-display text-lg font-semibold flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary"><CalendarIcon className="h-4 w-4" /></span>
            {c.monthTitle} <span className="text-muted-foreground font-normal">— {monthName}</span>
          </h2>
          <Badge variant="secondary" className="rounded-full">{data.current_month.total}</Badge>
        </div>

        {loading ? (
          <Skeleton className="h-24 w-full rounded-2xl" />
        ) : (data.current_month.birthdays.length === 0 && data.current_month.anniversaries.length === 0) ? (
          <Card className="rounded-2xl border-dashed bg-white/60">
            <CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="celebrations-month-empty">{c.monthEmpty}</CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <p className="font-serif-display text-base font-semibold flex items-center gap-2"><Cake className="h-4 w-4 text-primary" />{c.birthdaysHeader}</p>
              {data.current_month.birthdays.length === 0 ? (
                <Card className="rounded-2xl border-dashed bg-white/60"><CardContent className="py-6 text-center text-xs text-muted-foreground">—</CardContent></Card>
              ) : (
                data.current_month.birthdays.map((row) => (
                  <CelebrationCard
                    key={`mb-${row.id}`}
                    kind="birthday"
                    title={row.full_name}
                    subtitle={`${c.turning} ${row.age_on_birthday} ${c.years}`}
                    phone={row.phone}
                    date={row.date}
                    lang={lang}
                    nextOn={row.next_occurrence}
                  />
                ))
              )}
            </div>
            <div className="space-y-2">
              <p className="font-serif-display text-base font-semibold flex items-center gap-2"><Heart className="h-4 w-4 text-primary" />{c.anniversariesHeader}</p>
              {data.current_month.anniversaries.length === 0 ? (
                <Card className="rounded-2xl border-dashed bg-white/60"><CardContent className="py-6 text-center text-xs text-muted-foreground">—</CardContent></Card>
              ) : (
                data.current_month.anniversaries.map((row) => (
                  <CelebrationCard
                    key={`ma-${row.id}`}
                    kind="anniversary"
                    title={row.full_name}
                    subtitle={`${c.celebrating} ${row.years_on_anniversary} ${c.anniversaryYears}`}
                    phone={row.phone}
                    date={row.date}
                    lang={lang}
                    nextOn={row.next_occurrence}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function CelebrationCard({ kind, title, subtitle, phone, date, lang, accentToday, nextOn }) {
  const Icon = kind === "birthday" ? Cake : Heart;
  return (
    <Card
      className={`rounded-2xl ring-1 ring-black/5 ${accentToday ? "before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-primary relative overflow-hidden" : ""}`}
      data-testid={`celebration-card-${kind}`}
    >
      <CardContent className={`flex items-start gap-3 p-4 ${accentToday ? "pl-5" : ""}`}>
        <div className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-serif-display text-base font-semibold leading-tight">{title}</p>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><CalendarIcon className="h-3 w-3" />{fmtDate(nextOn || date, lang)}</span>
            {phone && (
              <a href={`tel:${phone}`} className="inline-flex items-center gap-1 hover:text-primary">
                <Phone className="h-3 w-3" />{phone}
              </a>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
